import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { AppID, Filter } from '@playq/octopus-common';
import { CopyEntityBrief, CopyEntitySort, QueryCopyEntityResponse } from '@playq/octopus2-creatives';
import { GenericFailure, OffsetLimit } from '@playq/services-shared';

import {
  IUseQueryOptionsExtended,
  useEitherQuery,
  useMutateQueryDataAfterEntityDeleting,
  useQueryPrefetch,
} from '/api/service-hooks';
import { services2 } from '/api/services2';
import { iteratorMaxLimit } from '/constants';
import { appToolkit } from '/store';

import { copyQueryTags } from './constants';

const emptyArray: [] = [];

const defaultQuery = {
  iterator: new OffsetLimit({ limit: iteratorMaxLimit, offset: 0 }),
  sortBy: [],
  filterBy: {},
};

export const useQueryCopy = (
  iterator: OffsetLimit = defaultQuery.iterator,
  sortBy: CopyEntitySort[] = defaultQuery.sortBy,
  filterBy: {
    [key: string]: Filter;
  } = defaultQuery.filterBy,
  options?: IUseQueryOptionsExtended<QueryCopyEntityResponse | undefined, GenericFailure | Error>
) => {
  const appID: AppID | undefined = useSelector(appToolkit.selectors.appID);

  const queryClient = useQueryClient();

  const keys = useMemo(
    () => copyQueryTags.concat(appID, iterator, sortBy, filterBy),
    [appID, iterator, sortBy, filterBy]
  );

  const isValid = useMemo(() => appID !== undefined, [appID]);
  const enabled = useMemo(() => (options?.enabled ?? true) && isValid, [options?.enabled, isValid]);
  const enablePrefetch = useMemo(
    () => (options?.enablePrefetch ?? true) && isValid,
    [options?.enablePrefetch, isValid]
  );

  const { mutate, refetch, ...res } = useEitherQuery(
    keys,
    () => services2.copiesService.queryCopy(appID as AppID, iterator, sortBy, filterBy),
    {
      placeholderData: keepPreviousData,
      ...options,
      enabled,
    }
  );

  const total = useMemo(() => res.data?.total ?? 0, [res.data?.total]);

  const { nextKeys } = useQueryPrefetch({
    keys,
    enabled: enablePrefetch,
    total,
    args: [appID as AppID, iterator, sortBy, filterBy],
    serviceMethod: services2.copiesService.queryCopy.bind(services2.copiesService),
  });

  const mutateDeletedCopy = useMutateQueryDataAfterEntityDeleting({
    queryClient,
    nextPageQueryKey: nextKeys,
    total,
    iterator,
    mutate,
    refetch,
    getID: (c?: CopyEntityBrief) => c?.id,
    getEntities: (r: QueryCopyEntityResponse) => r.entities,
    setEntities: (r: QueryCopyEntityResponse, newEntities: CopyEntityBrief[]) => {
      r.entities = newEntities;
    },
    createNewResponse: (prevResponse: QueryCopyEntityResponse) => new QueryCopyEntityResponse(prevResponse.serialize()),
  });

  const removeQueries = useCallback(() => {
    queryClient.removeQueries({ queryKey: copyQueryTags });
  }, [queryClient]);

  return {
    ...res,
    copies: res.data?.entities || emptyArray,
    total,
    tags: keys,
    mutateDeletedCopy,
    removeQueries,
    refetch,
  };
};
